/**
 * 관찰별 색 — **TIC 의 관찰 표시 전용**이다 (2026-09-06 이후).
 *
 * 이름 그대로 목업 시절(초기 커밋 a15eb4f) "D1 Cotton" 12 feature 를 위한
 * 표였다. 중복제거 해제 이후 관찰은 스캔 수만큼 늘어(운영 실측 170건)
 * F13 이상은 전부 아래 fallback 회색으로 떨어진다.
 *
 * **MZRT 산점도 점 색은 더 이상 이 표를 쓰지 않는다** — 저강도에서 미동정
 * 점과 섞였고(OKLab 1.6, 하한 15), 12색조차 색각 이상 all-pairs 를 통과하지
 * 못하며(F6 #8b5cf6 ↔ F12 #a855f7 은 protan 에서 사실상 같은 색), 애초에
 * 관찰은 x 위치가 구분해 준다(관찰 하나 = 스캔 하나 = RT 하나). 점 색은
 * `identifiedPointColor` 를 쓴다.
 *
 * 여기 손대면 TIC(`TICPlotUplot`)의 관찰 점선·rug 색이 조용히 따라 바뀐다 —
 * 그 파일에는 단위 테스트가 없다. 바꾸려면 그쪽 테스트를 먼저 세울 것.
 */
export const FEATURE_COLORS: Record<string, string> = {
  F1: "#10b981",
  F2: "#3b82f6",
  F3: "#6366f1",
  F4: "#ef4444",
  F5: "#f59e0b",
  F6: "#8b5cf6",
  F7: "#ec4899",
  F8: "#06b6d4",
  F9: "#84cc16",
  F10: "#f97316",
  F11: "#14b8a6",
  F12: "#a855f7",
};

export function getFeatureColor(featureId: string): string {
  return FEATURE_COLORS[featureId] || "#6b7280";
}

// 알케인 표준물질 RT 데이터 (RI 계산용)
export const alkaneStandards = [
  { carbon: 6, name: "n-Hexane", rt: 1.25 },
  { carbon: 7, name: "n-Heptane", rt: 2.85 },
  { carbon: 8, name: "n-Octane", rt: 4.2 },
  { carbon: 9, name: "n-Nonane", rt: 5.8 },
  { carbon: 10, name: "n-Decane", rt: 7.5 },
  { carbon: 11, name: "n-Undecane", rt: 9.2 },
  { carbon: 12, name: "n-Dodecane", rt: 11.0 },
];

// RT를 RI로 변환하는 함수
export function calculateRI(rt: number): number {
  let lowerAlkane = alkaneStandards[0];
  let upperAlkane = alkaneStandards[1];

  for (let i = 0; i < alkaneStandards.length - 1; i++) {
    if (rt >= alkaneStandards[i].rt && rt <= alkaneStandards[i + 1].rt) {
      lowerAlkane = alkaneStandards[i];
      upperAlkane = alkaneStandards[i + 1];
      break;
    }
  }

  const n = lowerAlkane.carbon;
  const tR_n = lowerAlkane.rt;
  const tR_n1 = upperAlkane.rt;
  const ratio = (rt - tR_n) / (tR_n1 - tR_n);
  const ri = 100 * (n + ratio);

  return Math.round(ri * 10) / 10;
}

// RI를 RT로 변환하는 함수
export function calculateRTFromRI(ri: number): number {
  const n = Math.floor(ri / 100);
  const fraction = (ri % 100) / 100;

  const lowerAlkane = alkaneStandards.find((a) => a.carbon === n);
  const upperAlkane = alkaneStandards.find((a) => a.carbon === n + 1);

  if (!lowerAlkane || !upperAlkane) return 0;

  const rt = lowerAlkane.rt + fraction * (upperAlkane.rt - lowerAlkane.rt);
  return Math.round(rt * 100) / 100;
}

// hex -> rgb 변환 (intensity 색상 보간에 사용)
function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

// 두 hex 색상 간 linear interpolation
function interpolateColor(color1: string, color2: string, factor: number) {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return color1;

  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * factor);
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * factor);
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * factor);

  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * 동정 점(= AI 후보가 기록된 스캔의 점) 강도 램프의 양 끝.
 *
 * 색조는 **부류**를 뜻한다("동정됨"), 명도는 그 스캔 안에서의 상대강도를
 * 뜻한다. 관찰 신원은 싣지 않는다 — 관찰은 x 위치가 구분한다.
 *
 * violet 을 고른 이유는 대비가 아니라 개체 일관성이다. TIC 크로마토그램
 * 선이 이미 이 색조이고(`TICPlotUplot` LINE_COLOR), MZRT 의 동정 점과 TIC
 * 의 봉우리는 같은 관찰을 가리킨다. 파랑은 스펙트럼 비교의 "참조"와 선택
 * 테두리(#2563eb)가 이미 점유했다.
 */
export const IDENTIFIED_RAMP_FROM = "#8b5cf6";
export const IDENTIFIED_RAMP_TO = "#5b21b6";

/**
 * 측정만 된 스캔(후보 없음)의 점 색 — 맥락 층.
 *
 * 종전 `#cbd5e1` 은 흰 배경 대비 1.48:1 로 격자(#f1f5f9)와도 겨우 구분됐고,
 * 무엇보다 저강도 동정 점들 **한가운데** 앉아 있었다. 이 값은 램프 하한과
 * OKLab 21.9(정상)·12.2(최악 색각) 떨어져 있으면서 대비도 2.56:1 로 올린다.
 */
export const MEASURED_ONLY_COLOR = "#94a3b8";

/**
 * 동정 점의 실제 색 — **차트 점 · 호버 강조 · 선택 강조가 공유한다.**
 *
 * 2026-09-04: `MZRTChartRecharts` 안에 있던 것을 여기로 옮겼다. 강조 표시를
 * "도트가 커지는" 형태로 바꾸면서 덧그림 층이 **점과 같은 색**을 다시 만들어야
 * 하게 됐다(운영자: "색깔을 동등 또는 동등한 색에서 강조하는 형태"). 차트와
 * 덧그림 두 곳이 같은 색을 써야 하므로 한 곳에 둔다 — 갈리면 커진 점의 색이
 * 원래 점과 달라진다.
 *
 * 2026-09-06: 인자에서 `featureId` 를 뺐다. 색이 관찰마다 달랐지만 실제로는
 * F1~F12 만 색이 있었고 나머지는 전부 같은 회색이었다 — 저강도에서 미동정
 * 점과 OKLab 1.6 까지 붙어(하한 15), 화면에서 가장 중요한 구분인 "동정됨 /
 * 측정만 됨"이 무너졌다. 계약을 이름에 적는다: 색은 **부류와 강도**만 뜻한다.
 *
 * 분모(`maxIntensity`)는 그 점이 속한 스캔의 최대 이온이다. 관찰마다 분모가
 * 다르므로 **다른 관찰끼리는 색을 비교할 수 없다** — 화면에도 그렇게 적는다
 * (`MZRTPlotLegendStrip` 의 INTENSITY_SCOPE_NOTE).
 */
export function identifiedPointColor(
  intensity: number,
  maxIntensity: number,
): string {
  // 분모를 모르면 램프 하한으로 그린다. 종전에는 흰색에 가까운 `#f8f9fa`
  // 로 떨어져(흰 배경과 ΔE00 1.38) **동정 사실이 화면에서 사라졌다** —
  // `featureMaxIntensities` 에 키가 없으면 호출부가 0 을 넘긴다.
  if (!Number.isFinite(intensity) || !maxIntensity || maxIntensity <= 0) {
    return IDENTIFIED_RAMP_FROM;
  }

  // 상한을 넘는 강도가 램프 밖으로 나가지 않게 죈다.
  const relativeIntensity = Math.min(Math.max(intensity / maxIntensity, 0), 1);

  return interpolateColor(
    IDENTIFIED_RAMP_FROM,
    IDENTIFIED_RAMP_TO,
    relativeIntensity,
  );
}
